import {
    ArrowRight,
    CheckCircle2,
    PawPrint,
    Search,
    CalendarCheck,
    Star,
    ShieldCheck,
    Sparkles,
    ExternalLink,
    Heart,
    Dog,
    Bone,
    Scissors,
    ScanLine,
} from 'lucide-react';
import { motion } from 'motion/react';
import { Button } from './ui/Button';
import { Contact } from './Contact';

const targetGroups = [
    'Hundefriseure',
    'Hundeschulen',
    'Hundezüchter',
    'Tierpensionen',
    'Tierphysio',
    'Mobile Tierbetreuung',
];

const benefits = [
    {
        icon: Search,
        title: 'Besser lokal gefunden werden',
        text: 'Deine Website wird so aufgebaut, dass Tierhalter schnell verstehen, was du anbietest und dich einfacher über Google finden können.',
    },
    {
        icon: ShieldCheck,
        title: 'Mehr Vertrauen aufbauen',
        text: 'Mit klaren Leistungen, echten Bildern, Bewertungen und einem professionellen Auftritt wirkst du direkt seriöser.',
    },
    {
        icon: CalendarCheck,
        title: 'Mehr Anfragen erhalten',
        text: 'Besucher sollen nicht suchen müssen. Sie finden Kontakt, Standort, Preise und Terminmöglichkeiten sofort.',
    },
];

const services = [
    'Moderne Website oder Landingpage',
    'Mobil optimiertes Design',
    'Texte für deine Zielgruppe',
    'Struktur für lokale Google-Sichtbarkeit',
    'Kontaktformular, WhatsApp oder Terminlink',
    'Leistungsseiten für deine Angebote',
    'Bewertungen, Galerie und Vertrauenselemente',
    'Technische Performance & saubere Umsetzung',
];

const processSteps = [
    {
        step: '01',
        title: 'Kostenloser Website-Check',
        text: 'Ich schaue mir deinen aktuellen Auftritt an und zeige dir, wo du online Anfragen verlierst.',
    },
    {
        step: '02',
        title: 'Strategie für deine Tierdienstleistung',
        text: 'Wir klären Zielgruppe, Leistungen, Standort, Suchbegriffe und den besten Aufbau deiner Seite.',
    },
    {
        step: '03',
        title: 'Design & Umsetzung',
        text: 'Ich baue dir eine moderne, schnelle und vertrauenswürdige Website, die zu deinem Angebot passt.',
    },
    {
        step: '04',
        title: 'Online gehen & sichtbar werden',
        text: 'Nach dem Launch bekommst du eine Website, die klar verkauft und langfristig erweitert werden kann.',
    },
];

const animalProjects = [
    {
        title: 'Website für eine Hundezucht',
        category: 'Hundezucht',
        icon: Dog,
        url: 'https://vom-schnuesterhus.de',
        description:
            'Ein vertrauensvoller Auftritt für eine Hundezucht – mit klaren Informationen, emotionaler Bildsprache und professioneller Präsentation.',
        highlights: ['Vertrauen aufbauen', 'Zucht seriös präsentieren', 'Mobile optimiert'],
    },
    {
        title: 'Website für eine Hundefriseurin',
        category: 'Hundefriseur',
        icon: Scissors,
        url: 'https://hundesalon-kj.de',
        description:
            'Eine moderne Website für einen Hundesalon – mit klaren Leistungen, sympathischer Darstellung und einfachem Anfrageweg.',
        highlights: ['Leistungen klar zeigen', 'Anfragen erleichtern', 'Lokale Kunden gewinnen'],
    },
    {
        title: 'Website für eine digitale Hundemarke',
        category: 'NFC Hundemarke',
        icon: ScanLine,
        url: 'https://nfcdog.de',
        description:
            'Ein digitaler Produktauftritt für eine Hundemarke mit NFC-Technologie – modern, erklärend und auf Vertrauen sowie einfache Nutzung ausgelegt.',
        highlights: ['NFC-Technologie erklärt', 'Produkt klar positioniert', 'Mobile First'],
    },
];

const scrollToContact = () => {
    const el = document.querySelector('#kontakt');
    if (!el) return;

    const y = el.getBoundingClientRect().top + window.scrollY - 90;
    window.scrollTo({ top: y, behavior: 'smooth' });
};

export function WebdesignTierdienstleisterPage() {
    return (
        <main className="overflow-hidden bg-[#fffaf1]">
            {/* Hero */}
            <section className="relative min-h-screen pt-36 pb-20 bg-[radial-gradient(circle_at_top_left,#fde68a_0,transparent_34%),linear-gradient(135deg,#fff7ed_0%,#f7fee7_45%,#f0fdf4_100%)]">
                <div className="pointer-events-none absolute inset-0 overflow-hidden">
                    <div className="absolute left-[-120px] top-32 h-72 w-72 rounded-full bg-lime-300/30 blur-3xl" />
                    <div className="absolute right-[-120px] top-56 h-80 w-80 rounded-full bg-emerald-300/30 blur-3xl" />
                    <div className="absolute bottom-0 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-amber-300/25 blur-3xl" />

                    <PawPrint className="absolute left-[8%] top-[24%] h-10 w-10 rotate-[-18deg] text-amber-300/40" />
                    <PawPrint className="absolute right-[12%] top-[22%] h-12 w-12 rotate-[16deg] text-emerald-300/40" />
                    <Bone className="absolute bottom-[18%] left-[14%] h-10 w-10 rotate-[18deg] text-lime-300/40" />
                </div>

                <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 lg:grid-cols-[1.1fr_0.9fr]">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.55, ease: 'easeOut' }}
                    >
                        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-emerald-700/10 bg-white/80 px-4 py-2 text-sm font-medium text-emerald-800 shadow-sm backdrop-blur">
                            <PawPrint className="h-4 w-4 text-amber-600" />
                            Webdesign für Tierdienstleister
                        </div>

                        <h1 className="max-w-4xl text-4xl font-bold tracking-tight text-stone-950 md:text-6xl lg:text-7xl">
                            Websites für Tierdienstleister, die Vertrauen schaffen und Anfragen bringen.
                        </h1>

                        <p className="mt-6 max-w-2xl text-lg leading-8 text-stone-700 md:text-xl">
                            Ich entwickle moderne Websites für Hundefriseure, Hundeschulen, Züchter und lokale
                            Tierbetriebe – warm, professionell, mobil optimiert und darauf ausgelegt, aus Besuchern
                            echte Kundenanfragen zu machen.
                        </p>

                        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                            <Button
                                onClick={scrollToContact}
                                className="group bg-gradient-to-r from-emerald-700 via-lime-600 to-amber-500 hover:from-emerald-800 hover:via-lime-700 hover:to-amber-600 text-white shadow-lg"
                            >
                                <span className="text-white">Kostenlosen Website-Check anfragen</span>
                                <ArrowRight className="ml-2 h-4 w-4 text-white transition-transform group-hover:translate-x-0.5" />
                            </Button>

                            <a
                                href="#tierprojekte"
                                className="inline-flex items-center justify-center rounded-xl border border-emerald-900/10 bg-white/80 px-5 py-3 text-sm font-semibold text-stone-900 shadow-sm backdrop-blur transition hover:bg-white"
                            >
                                Beispiele ansehen
                            </a>
                        </div>

                        <div className="mt-8 flex flex-wrap gap-3">
                            {targetGroups.slice(0, 4).map((group) => (
                                <span
                                    key={group}
                                    className="rounded-full border border-emerald-900/10 bg-white/75 px-3 py-1.5 text-sm text-stone-700 shadow-sm"
                                >
                                    {group}
                                </span>
                            ))}
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.96, y: 24 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{ duration: 0.65, ease: 'easeOut', delay: 0.1 }}
                        className="relative"
                    >
                        <div className="absolute -right-4 -top-4 z-10 rounded-2xl bg-white px-4 py-3 shadow-xl">
                            <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
                                <Heart className="h-4 w-4 fill-amber-400 text-amber-400" />
                                Tierfreundlich & vertrauensvoll
                            </div>
                        </div>

                        <div className="relative rounded-[2rem] border border-emerald-900/10 bg-white/80 p-4 shadow-2xl backdrop-blur">
                            <div className="rounded-[1.5rem] border border-emerald-900/5 bg-gradient-to-br from-white to-lime-50 p-5">
                                <div className="flex items-center justify-between border-b border-emerald-900/10 pb-4">
                                    <div>
                                        <div className="text-sm font-semibold text-stone-900">
                                            Hundesalon Fellglück
                                        </div>
                                        <div className="text-xs text-stone-500">
                                            Beispiel-Aufbau für Tierdienstleister
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">
                                        <Star className="h-3 w-3 fill-current" />
                                        4.9
                                    </div>
                                </div>

                                <div className="mt-5 grid gap-3">
                                    <div className="rounded-2xl bg-gradient-to-r from-emerald-700 via-lime-600 to-amber-500 p-5 text-white">
                                        <div className="text-xs font-medium uppercase tracking-wide text-white/80">
                                            Website-Ziel
                                        </div>
                                        <div className="mt-2 text-2xl font-semibold leading-tight">
                                            Mehr Buchungen über Google & Website
                                        </div>
                                    </div>

                                    <div className="grid gap-3 sm:grid-cols-2">
                                        <div className="rounded-2xl border border-emerald-900/5 bg-white p-4">
                                            <div className="text-xs text-stone-500">Leistungen</div>
                                            <div className="mt-1 text-sm font-semibold text-stone-900">
                                                Schneiden, Trimmen, Pflege
                                            </div>
                                        </div>

                                        <div className="rounded-2xl border border-emerald-900/5 bg-white p-4">
                                            <div className="text-xs text-stone-500">Kontakt</div>
                                            <div className="mt-1 text-sm font-semibold text-stone-900">
                                                WhatsApp & Anfrageformular
                                            </div>
                                        </div>
                                    </div>

                                    <div className="rounded-2xl border border-emerald-900/5 bg-white p-4">
                                        <div className="mb-3 flex items-center justify-between">
                                            <div className="text-sm font-semibold text-stone-900">
                                                Vertrauenselemente
                                            </div>
                                            <Sparkles className="h-4 w-4 text-amber-500" />
                                        </div>

                                        <div className="space-y-2">
                                            {['Bewertungen sichtbar', 'Preise klar erklärt', 'Bilder & Galerie'].map((item) => (
                                                <div key={item} className="flex items-center gap-2 text-sm text-stone-600">
                                                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                                                    {item}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Zielgruppen */}
            <section className="bg-white py-20">
                <div className="mx-auto max-w-7xl px-4">
                    <div className="mx-auto max-w-3xl text-center">
                        <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-lime-50 px-4 py-2 text-sm font-medium text-emerald-800">
                            <PawPrint className="h-4 w-4 text-amber-600" />
                            Für wen ist das?
                        </div>

                        <h2 className="text-3xl font-bold tracking-tight text-stone-950 md:text-5xl">
                            Für Tierdienstleister, die online professioneller auftreten wollen.
                        </h2>

                        <p className="mt-5 text-lg leading-8 text-stone-600">
                            Egal ob du lokal gebucht wirst, Beratung anbietest oder Vertrauen über Bilder und
                            Bewertungen aufbauen musst: Deine Website sollte sofort zeigen, warum Kunden bei dir richtig sind.
                        </p>
                    </div>

                    <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {targetGroups.map((group) => (
                            <div
                                key={group}
                                className="group rounded-3xl border border-emerald-900/5 bg-[#fffaf1] p-6 transition hover:-translate-y-1 hover:bg-lime-50 hover:shadow-lg"
                            >
                                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-emerald-700 shadow-sm">
                                    <PawPrint className="h-5 w-5" />
                                </div>

                                <h3 className="text-lg font-semibold text-stone-950">{group}</h3>

                                <p className="mt-2 text-sm leading-6 text-stone-600">
                                    Ein klarer Online-Auftritt, der Leistungen, Vertrauen und Kontaktmöglichkeiten
                                    verständlich zusammenbringt.
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Echte Beispiele */}
            <section id="tierprojekte" className="relative bg-gradient-to-br from-lime-50 via-white to-amber-50 py-20">
                <div className="pointer-events-none absolute inset-0 overflow-hidden">
                    <PawPrint className="absolute left-[6%] top-16 h-12 w-12 rotate-[-18deg] text-emerald-200/70" />
                    <Bone className="absolute right-[7%] bottom-20 h-12 w-12 rotate-[18deg] text-amber-200/80" />
                </div>

                <div className="relative mx-auto max-w-7xl px-4">
                    <div className="mx-auto max-w-3xl text-center">
                        <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-emerald-800 shadow-sm">
                            <Sparkles className="h-4 w-4 text-amber-500" />
                            Echte Projekte aus der Tierbranche
                        </div>

                        <h2 className="text-3xl font-bold tracking-tight text-stone-950 md:text-5xl">
                            Drei Beispiele, die zeigen: Ich habe diese Nische bereits umgesetzt.
                        </h2>

                        <p className="mt-5 text-lg leading-8 text-stone-600">
                            Statt nur über Tierdienstleister zu sprechen, zeige ich echte Arbeiten: eine Website für eine
                            Hundezucht, eine Website für eine Hundefriseurin und einen digitalen Produktauftritt für eine
                            NFC-Hundemarke.
                        </p>
                    </div>

                    <div className="mt-12 grid gap-8 lg:grid-cols-3">
                        {animalProjects.map((project, index) => {
                            const Icon = project.icon;

                            return (
                                <motion.article
                                    key={project.title}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                    transition={{ duration: 0.45, delay: index * 0.08 }}
                                    className="overflow-hidden rounded-[2rem] border border-emerald-900/10 bg-white shadow-xl"
                                >
                                    <div className="border-b border-emerald-900/10 bg-stone-100 px-4 py-3">
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="flex items-center gap-2">
                                                <span className="h-3 w-3 rounded-full bg-red-400" />
                                                <span className="h-3 w-3 rounded-full bg-yellow-400" />
                                                <span className="h-3 w-3 rounded-full bg-green-400" />
                                            </div>

                                            <div className="hidden max-w-[62%] truncate rounded-full bg-white px-3 py-1 text-xs text-stone-500 sm:block">
                                                {project.url}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="relative bg-gradient-to-br from-lime-100 to-amber-100 p-4 sm:p-0">
                                        {/* Mobile Smartphone Preview */}
                                        <div className="relative mx-auto h-[680px] max-w-[310px] overflow-hidden rounded-[2rem] border-[10px] border-stone-900 bg-white shadow-2xl sm:hidden">
                                            <div className="absolute left-1/2 top-2 z-10 h-1.5 w-20 -translate-x-1/2 rounded-full bg-stone-700" />

                                            <iframe
                                                src={project.url}
                                                title={project.title}
                                                loading="lazy"
                                                className="h-full w-full border-0"
                                            />

                                            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-stone-950/20 to-transparent" />
                                        </div>

                                        {/* Tablet/Desktop Browser Preview */}
                                        <div className="relative hidden aspect-[16/10] overflow-hidden sm:block">
                                            <iframe
                                                src={project.url}
                                                title={project.title}
                                                loading="lazy"
                                                className="h-full w-full border-0"
                                            />

                                            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-stone-950/25 to-transparent" />
                                        </div>
                                    </div>

                                    <div className="p-6">
                                        <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-lime-50 px-3 py-1.5 text-xs font-medium text-emerald-800">
                                            <Icon className="h-3.5 w-3.5 text-amber-600" />
                                            {project.category}
                                        </div>

                                        <h3 className="text-2xl font-semibold tracking-tight text-stone-950">
                                            {project.title}
                                        </h3>

                                        <p className="mt-3 text-sm leading-7 text-stone-600">
                                            {project.description}
                                        </p>

                                        <div className="mt-5 flex flex-wrap gap-2">
                                            {project.highlights.map((highlight) => (
                                                <span
                                                    key={highlight}
                                                    className="rounded-full border border-emerald-900/10 bg-[#fffaf1] px-3 py-1 text-xs text-stone-600"
                                                >
                                                    {highlight}
                                                </span>
                                            ))}
                                        </div>

                                        <a
                                            href={project.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-emerald-800 transition hover:text-emerald-950"
                                        >
                                            Website live ansehen
                                            <ExternalLink className="h-4 w-4" />
                                        </a>
                                    </div>
                                </motion.article>
                            );
                        })}
                    </div>

                    <div className="mx-auto mt-8 max-w-3xl rounded-3xl border border-amber-500/15 bg-white/80 p-5 text-center text-sm leading-6 text-stone-600 shadow-sm backdrop-blur">
                        Hinweis: Falls eine Live-Vorschau im Browser blockiert wird, liegt das meist an Sicherheitsheadern
                        der jeweiligen Website. Der Button zur Live-Website funktioniert trotzdem.
                    </div>
                </div>
            </section>

            {/* Benefits */}
            <section className="bg-white py-20">
                <div className="mx-auto max-w-7xl px-4">
                    <div className="max-w-3xl">
                        <h2 className="text-3xl font-bold tracking-tight text-stone-950 md:text-5xl">
                            Deine Website soll nicht nur schön aussehen. Sie soll Vertrauen auslösen.
                        </h2>

                        <p className="mt-5 text-lg leading-8 text-stone-600">
                            Tierhalter entscheiden emotional, aber auch schnell. Wenn deine Website unklar,
                            veraltet oder schwer nutzbar ist, verlierst du Anfragen an sichtbarere Anbieter.
                        </p>
                    </div>

                    <div className="mt-12 grid gap-6 md:grid-cols-3">
                        {benefits.map((benefit) => {
                            const Icon = benefit.icon;

                            return (
                                <div
                                    key={benefit.title}
                                    className="rounded-3xl border border-emerald-900/5 bg-[#fffaf1] p-7 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
                                >
                                    <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-700 via-lime-600 to-amber-500 text-white">
                                        <Icon className="h-6 w-6" />
                                    </div>

                                    <h3 className="text-xl font-semibold text-stone-950">
                                        {benefit.title}
                                    </h3>

                                    <p className="mt-3 text-sm leading-7 text-stone-600">
                                        {benefit.text}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Leistungen */}
            <section id="leistungen-tierdienstleister" className="bg-gradient-to-br from-[#fffaf1] to-lime-50 py-20">
                <div className="mx-auto grid max-w-7xl gap-12 px-4 lg:grid-cols-[0.9fr_1.1fr]">
                    <div>
                        <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-emerald-800 shadow-sm">
                            <Sparkles className="h-4 w-4 text-amber-500" />
                            Was du bekommst
                        </div>

                        <h2 className="text-3xl font-bold tracking-tight text-stone-950 md:text-5xl">
                            Eine Website, die deine Tierdienstleistung klar positioniert.
                        </h2>

                        <p className="mt-5 text-lg leading-8 text-stone-600">
                            Ich kombiniere Design, Struktur, Texte und Technik so, dass dein Angebot verständlich,
                            sympathisch und professionell wirkt.
                        </p>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                        {services.map((service) => (
                            <div
                                key={service}
                                className="flex items-start gap-3 rounded-2xl border border-emerald-900/5 bg-white p-4 shadow-sm"
                            >
                                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
                                <span className="text-sm font-medium leading-6 text-stone-800">
                                    {service}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Ablauf */}
            <section
                id="ablauf-tierdienstleister"
                className="bg-gradient-to-br from-emerald-950 via-stone-950 to-lime-950 py-20 text-white"
            >
                <div className="mx-auto max-w-7xl px-4">
                    <div className="max-w-3xl">
                        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-4 py-2 text-sm font-medium text-white">
                            <CalendarCheck className="h-4 w-4 text-amber-300" />
                            Der Ablauf
                        </div>

                        <h2 className="text-3xl font-bold tracking-tight md:text-5xl">
                            Klarer Prozess. Keine Technik-Verwirrung.
                        </h2>

                        <p className="mt-5 text-lg leading-8 text-white/70">
                            Du musst keine Website planen können. Ich führe dich durch die wichtigen Entscheidungen
                            und übersetze dein Angebot in einen starken Online-Auftritt.
                        </p>
                    </div>

                    <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
                        {processSteps.map((item) => (
                            <div
                                key={item.step}
                                className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur"
                            >
                                <div className="mb-5 text-sm font-semibold text-amber-300">
                                    {item.step}
                                </div>

                                <h3 className="text-xl font-semibold">
                                    {item.title}
                                </h3>

                                <p className="mt-3 text-sm leading-7 text-white/65">
                                    {item.text}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-white py-20">
                <div className="mx-auto max-w-7xl px-4">
                    <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-r from-emerald-700 via-lime-600 to-amber-500 p-8 text-white shadow-2xl md:p-12">
                        <PawPrint className="absolute right-10 top-10 h-20 w-20 rotate-12 text-white/10" />
                        <Bone className="absolute bottom-8 right-36 h-16 w-16 -rotate-12 text-white/10" />

                        <div className="relative max-w-3xl">
                            <h2 className="text-3xl font-bold tracking-tight md:text-5xl">
                                Lass uns prüfen, wie dein Tierbusiness online mehr Anfragen bekommen kann.
                            </h2>

                            <p className="mt-5 text-lg leading-8 text-white/85">
                                Ich schaue mir deinen aktuellen Auftritt an und zeige dir konkrete Verbesserungen für
                                Website, Google-Sichtbarkeit und Kontaktweg.
                            </p>

                            <Button
                                onClick={scrollToContact}
                                className="mt-8 group bg-white text-stone-900 hover:bg-stone-100 shadow-lg"
                            >
                                <span>Kostenlosen Website-Check starten</span>
                                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            <Contact />
        </main>
    );
}